import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { saveAs } from 'file-saver';

import { environment } from '@env/environment';

/**
 * Thin client de la generación CENTRALIZADA del PDF de cotización (api-rendafin,
 * ADR-0005). Reemplaza por completo al `PdfQuotationService` (jsPDF local) — ya
 * NO existe generación local de PDF en el panel.
 *
 * Dos endpoints según el tipo de cotización:
 *  - `generateQuotationPdf(parametersId)` → `GET /quotation-parameters/:id/pdf`
 *    para cotizaciones editables (`QuotationParameters`). El backend recalcula
 *    con `formulasService.createQuote(qpId)`; el caller NO pre-calcula nada.
 *  - `generateQuotationPdfById(quotationId)` → `GET /quotations/:id/pdf`
 *    para cotizaciones guardadas e inmutables (`Quotation`); renderiza el snapshot.
 *
 * Ambos devuelven el mismo PDF (mismo render backend) → cotizaciones idénticas
 * en panel, canal y mobile.
 */
@Injectable({
  providedIn: 'root',
})
export class PdfQuotationApiService {
  constructor(private readonly http: HttpClient) {}

  /**
   * Descarga el PDF y lo guarda con el nombre del header
   * `Content-Disposition` (RFC 5987 — preserva acentos en
   * `Cotización - Cliente - Activo.pdf`).
   */
  /** PDF de una cotización editable (`QuotationParameters`) → `GET /quotation-parameters/:id/pdf`. */
  async generateQuotationPdf(
    parametersId: string,
    options: { includeResidual?: boolean } = {}
  ): Promise<void> {
    await this.downloadPdf(`${environment.apiUrl}quotation-parameters/${parametersId}/pdf`, options);
  }

  /** PDF de una cotización guardada e inmutable (`Quotation`) → `GET /quotations/:id/pdf`. */
  async generateQuotationPdfById(
    quotationId: string,
    options: { includeResidual?: boolean } = {}
  ): Promise<void> {
    await this.downloadPdf(`${environment.apiUrl}quotations/${quotationId}/pdf`, options);
  }

  /** Descarga el blob del endpoint y lo guarda con el filename del header `Content-Disposition`. */
  private async downloadPdf(
    url: string,
    options: { includeResidual?: boolean }
  ): Promise<void> {
    const params: Record<string, string> = {};
    if (options.includeResidual !== undefined) {
      params['includeResidual'] = String(options.includeResidual);
    }

    const response = await firstValueFrom(
      this.http.get(url, {
        params,
        responseType: 'blob',
        observe: 'response',
      })
    );

    if (!response.body) {
      throw new Error('El servidor no entregó el PDF (body vacío)');
    }

    const fileName = this.extractFilenameFromHeaders(response) ?? 'Cotización.pdf';
    saveAs(response.body, fileName);
  }

  /**
   * Decodifica el filename del header `Content-Disposition`. El backend
   * envía dos parámetros (RFC 5987):
   *   - `filename="..."` — fallback ASCII URL-encoded
   *   - `filename*=UTF-8''...` — variante con encoding explícito
   * Preferimos UTF-8 para preservar tildes.
   */
  private extractFilenameFromHeaders(
    response: HttpResponse<Blob>
  ): string | null {
    const raw =
      response.headers.get('content-disposition') ??
      response.headers.get('Content-Disposition');
    if (!raw) return null;

    const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(raw);
    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(utf8Match[1].trim());
      } catch {
        // si el decode falla, caemos al filename clásico abajo
      }
    }

    const fallbackMatch = /filename="?([^";]+)"?/i.exec(raw);
    if (fallbackMatch?.[1]) {
      try {
        return decodeURIComponent(fallbackMatch[1].trim());
      } catch {
        return fallbackMatch[1].trim();
      }
    }

    return null;
  }
}
